import { useEventListener } from '@vueuse/core';
import type { TimeoutHandle } from '@mts-fe/types';
import type { Directive } from 'vue';
import { copyText } from '@mts-fe/utils';

export interface OnLongPressOptions {
  /**
   * Time in ms till `longpress` gets called
   *
   * @default 1000
   */
  delay?: number;

  modifiers?: { self?: boolean };
}

export function onLongPress(
  target: HTMLElement,
  handler: (event: PointerEvent) => void,
  options?: OnLongPressOptions,
) {
  let timeout: TimeoutHandle | undefined;

  function clear() {
    target.style.backgroundColor = 'inherit';
    if (timeout) {
      clearTimeout(timeout);
      timeout = undefined;
    }
  }

  function onDown(e: PointerEvent) {
    if (options?.modifiers?.self && e.target !== target) return;

    clear();
    e.preventDefault();
    e.stopPropagation();

    target.style.backgroundColor = '#e9fffb';
    timeout = setTimeout(() => handler(e), options?.delay ?? 1000);
  }

  const cleanup = [
    useEventListener(target, 'pointerdown', onDown),
    useEventListener(target, ['pointerup', 'pointerleave'], clear),
  ].filter(Boolean);

  const stop = () => cleanup.forEach((fn) => fn());

  return stop;
}

const pressCopyDirective: Directive = {
  mounted(el, binding) {
    onLongPress(el, () => {
      copyText(binding.value || el.textContent);
    });
  },
};

export default pressCopyDirective;
